import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines } from "../../lib/motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-black"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/stadium-poster.jpg"
          data-testid="hero-video"
        >
          <source src="/stadium.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full mx-auto max-w-[1600px] px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-6 flex items-center gap-3 text-white/80"
        >
          <span className="h-px w-10 bg-white/60" />
          <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold">
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em]">
          <RevealLines lines={[t.hero.titleLines[0]]} delay={0.3} testId="hero-line-1" />
          <span className="flex items-baseline gap-[0.15em] flex-wrap">
            <RevealLines lines={[t.hero.titleLines[1]]} delay={0.45} />
          </span>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.a
            href="#agence"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#agence")?.scrollIntoView({ behavior: "smooth" });
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            data-testid="hero-scroll-cta"
            className="group inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.25em] font-bold"
          >
            <span className="flex h-12 w-12 items-center justify-center border border-white/50 group-hover:bg-white group-hover:text-black transition-colors duration-300">
              <ArrowDown size={18} className="animate-bounce" />
            </span>
            {t.hero.discover}
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
